import { ABaseModel } from '../abstracts/ABaseModel.js';
import { PageMeta } from './Meta.js';
import {
  ICmsModuleRendered,
  ICmsContentColumn,
  ICmsContentSection,
  ICmsPage,
  ICmsPageListResult,
  ICmsPagePaginationResult,
  ICmsPageRendered,
  ICmsPageLocale,
  ICmsPagePlaceholder,
  ICmsPagePlaceholderPaginationResult,
  ICmsModule,
  ICmsModulePaginationResult,
  ICmsAttribute,
  ICmsAttributePaginationResult,
  ICmsModuleInstance,
  ICmsModuleInstancePaginationResult,
  ICmsAttributeInstance,
  ICmsAttributeInstancePaginationResult,
  ICmsAttributeInstanceLocale,
  ICmsView,
  ICmsViewPaginationResult,
  ICmsViewPlaceholder,
  ICmsViewPlaceholderPaginationResult,
  ICmsLayout,
  ICmsLayoutPaginationResult,
  ICmsLayoutPlaceholder,
  ICmsLayoutPlaceholderPaginationResult,
} from '../interfaces/Cms.js';
import { PaginationResult, ListResult } from './List.js';

/**
 * CmsModuleRendered
 */
class CmsModuleRendered extends ABaseModel implements ICmsModuleRendered {
  placeholder: string;
  module: string;
  content: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPageRendered
 */
class CmsPageRendered extends PageMeta implements ICmsPageRendered {
  layout: string;
  locales: Array<CmsPageLocale>;
  placeholders: Array<CmsModuleRendered>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPage
 */
class CmsPage extends PageMeta implements ICmsPage {
  id: number;
  status: number;
  name: string;
  description: string;
  layoutId: number;
  layoutCode: string;
  layoutName: string;
  layoutStatus: number;
  locales: Array<CmsPageLocale>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPageLocale
 */
class CmsPageLocale extends ABaseModel implements ICmsPageLocale {
  localeCode: string;
  uri: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPageListResult
 */
class CmsPageListResult extends ListResult implements ICmsPageListResult {
  declare items: Array<CmsPage>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPagePaginationResult
 */
class CmsPagePaginationResult extends PaginationResult implements ICmsPagePaginationResult {
  declare items: Array<CmsPage>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPagePlaceholder
 */
class CmsPagePlaceholder extends ABaseModel implements ICmsPagePlaceholder {
  id: number;
  pageId: number;
  placeholderId: number;
  placeholder: Array<CmsLayoutPlaceholder>;
  moduleInstanceId: number;
  moduleInstance: Array<CmsModuleInstance>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsPagePlaceholderPaginationResult
 */
class CmsPagePlaceholderPaginationResult extends PaginationResult implements ICmsPagePlaceholderPaginationResult {
  declare items: Array<CmsPagePlaceholder>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsContentSection
 */
class CmsContentColumn extends ABaseModel implements ICmsContentColumn {
  column: number;
  sections: Array<CmsContentSection>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsContentSection
 */
class CmsContentSection extends ABaseModel implements ICmsContentSection {
  title: string;
  content: Array<string>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsModule
 */
class CmsModule extends ABaseModel implements ICmsModule {
  id: number;
  status: number;
  name: string;
  description: string;
  cache: boolean;
  zendModuleName: string;
  zendControllerName: string;
  zendActionName: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsModulePaginationResult
 */
class CmsModulePaginationResult extends PaginationResult implements ICmsModulePaginationResult {
  declare items: Array<CmsModule>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsModuleInstance
 */
class CmsModuleInstance extends ABaseModel implements ICmsModuleInstance {
  id: number;
  status: number;
  name: string;
  cache: boolean;
  moduleId: number;
  module: ICmsModule;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsModuleInstancePaginationResult
 */
class CmsModuleInstancePaginationResult extends PaginationResult implements ICmsModuleInstancePaginationResult {
  declare items: Array<CmsModuleInstance>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsAttribute
 */
class CmsAttribute extends ABaseModel implements ICmsAttribute {
  id: number;
  moduleId: number;
  name: string;
  type: number;
  rquired: boolean;
  localizable: boolean;
  structure: Record<string, any> | null;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsAttributePaginationResult
 */
class CmsAttributePaginationResult extends PaginationResult implements ICmsAttributePaginationResult {
  declare items: Array<CmsAttribute>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsAttributeInstanceLocale
 */
class CmsAttributeInstanceLocale extends ABaseModel implements ICmsAttributeInstanceLocale {
  id: number;
  locale: string | null;
  value: string;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsAttributeInstance
 */
class CmsAttributeInstance extends ABaseModel implements ICmsAttributeInstance {
  id: string;
  moduleInstanceId: string;
  attributeId: string;
  name: string;
  type: number;
  structure: Record<string, any> | null;
  value: string;
  required: boolean;
  localizable: boolean;
  locales: Array<CmsAttributeInstanceLocale>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsAttributeInstancePaginationResult
 */
class CmsAttributeInstancePaginationResult extends PaginationResult implements ICmsAttributeInstancePaginationResult {
  declare items: Array<CmsAttributeInstance>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsView
 */
class CmsView extends ABaseModel implements ICmsView {
  id: number;
  name: string;
  description: string;
  disable: boolean;
  zendModuleName: string;
  zendControllerName: string;
  zendActionName: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsViewPaginationResult
 */
class CmsViewPaginationResult extends PaginationResult implements ICmsViewPaginationResult {
  declare items: Array<CmsView>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsViewPlaceholder
 */
class CmsViewPlaceholder extends ABaseModel implements ICmsViewPlaceholder {
  id: number;
  viewId: number;
  name: string;
  description: string;
  key: string;
  moduleInstanceId: number;
  moduleInstance: CmsModuleInstance;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsViewPlaceholderPaginationResult
 */
class CmsViewPlaceholderPaginationResult extends PaginationResult implements ICmsViewPlaceholderPaginationResult {
  declare items: Array<CmsViewPlaceholder>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsLayout
 */
class CmsLayout extends ABaseModel implements ICmsLayout {
  id: number;
  status: number;
  name: string;
  description: string;
  code: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsLayoutPaginationResult
 */
class CmsLayoutPaginationResult extends PaginationResult implements ICmsLayoutPaginationResult {
  declare items: Array<CmsLayout>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsLayoutPlaceholder
 */
class CmsLayoutPlaceholder extends ABaseModel implements ICmsLayoutPlaceholder {
  id: number;
  layoutId: number;
  name: string;
  description: string;
  key: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * CmsLayoutPlaceholderPaginationResult
 */
class CmsLayoutPlaceholderPaginationResult extends PaginationResult implements ICmsLayoutPlaceholderPaginationResult {
  declare items: Array<CmsLayoutPlaceholder>;

  /**
   * Constructor
   */
  constructor() {
    super();
  }
}

export {
  CmsModuleRendered,
  CmsContentSection,
  CmsContentColumn,
  CmsPageRendered,
  CmsPage,
  CmsPageLocale,
  CmsPageListResult,
  CmsPagePaginationResult,
  CmsPagePlaceholder,
  CmsPagePlaceholderPaginationResult,
  CmsModule,
  CmsModulePaginationResult,
  CmsModuleInstance,
  CmsModuleInstancePaginationResult,
  CmsAttribute,
  CmsAttributePaginationResult,
  CmsAttributeInstanceLocale,
  CmsAttributeInstance,
  CmsAttributeInstancePaginationResult,
  CmsView,
  CmsViewPaginationResult,
  CmsViewPlaceholder,
  CmsViewPlaceholderPaginationResult,
  CmsLayout,
  CmsLayoutPaginationResult,
  CmsLayoutPlaceholder,
  CmsLayoutPlaceholderPaginationResult,
};
